import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
/**
 * Raw BILLING row as returned by invoice-api /billings endpoints.
 * Mirrors the DB2 BILLING table used by the legacy KundenBilling admin screen.
 */
export interface BillingInformation {
  id: number;
  feature: string;
  featureShowName: string | null;
  info: string | null;
  billingTime: string;
  unitPrice: number;
  amount: number;
  sum: number;
  userAccountId: number;
  documentId: number | null;
  invoiceId: number | null;
  billingGroupId: number | null;
}

export interface BillingGroup {
  id: number;
  /** Backend-managed code, e.g. 'creditGroup', 'secretarialServiceGroup'. */
  name: string;
  /** Human-readable label; falls back to `name` when null. */
  description: string | null;
  order: number;
  features: BillingGroupFeature[];
}

export interface BillingGroupFeature {
  /** Feature code used in POST body `featureName`. */
  feature: string;
  /** Optional display label. Often null on live data; use `feature` as fallback. */
  showName?: string | null;
  billingGroupId?: number;
  /** Legacy per-feature enablement flag. `false` → hide/disable in Feature dropdown. */
  entryEditable?: boolean;
}

interface PageBillingInformation {
  content: BillingInformation[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  last: boolean;
}

export interface BillingMessage {
  featureName: string;
  customerId?: string;
  userAccountId: number;
  chargeOn: string;
  unitPrice: number;
  amount: number;
  additionalInfo?: string;
}

export interface BillingCreated {
  id: number;
  featureName: string;
  userAccountId: number;
  chargeOn: string;
  unitPrice: number;
  amount: number;
  additionalInfo: string | null;
  status: 'CREATED' | 'PROBLEM';
}

/** Response of `DELETE /billings?documentId=&userAccountId=` (EB-4383). */
export interface CallcenterChargesDeleted {
  deletedCount: number;
}

@Injectable({ providedIn: 'root' })
export class CustomerBillingService {
  private readonly http = inject(HttpClient);

  private readonly base = environment.invoiceUrl;

  /**
   * Cached stream for /billings/groups. The groups + their features are static
   * catalog data, identical for every customer, so one fetch per session is enough.
   * Used by the Add Billing dialog — also pre-warmed from CustomerBillingComponent
   * so the dialog opens instantly.
   */
  private groups$?: Observable<BillingGroup[]>;

  /**
   * Cache of default feature prices — keyed by `customerId|sortedFeatureNames`.
   * Same feature set for the same customer returns the same observable so the
   * Add Billing dialog can be reopened without re-fetching.
   */
  private readonly defaultPricesCache = new Map<string, Observable<Map<string, number>>>();

  /** Static billing groups (categories for the Anzeigen-von dropdown). */
  getGroups(): Observable<BillingGroup[]> {
    if (!this.groups$) {
      this.groups$ = this.http
        .get<BillingGroup[]>(`${this.base}/billings/groups`)
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }
    return this.groups$;
  }

  /** Feature names addable via the Art dropdown for a specific user account. */
  getFeatures(userAccountId: number): Observable<string[]> {
    return this.http.get<string[]>(`${this.base}/billings/features`, {
      params: { userAccountId: String(userAccountId) },
    });
  }

  /**
   * Search billings for a single user account.
   * Page size is capped large enough to fit a typical month in one response.
   */
  search(params: {
    userAccountId: number;
    from: string;
    to: string;
    groupId?: number;
    page?: number;
    size?: number;
  }): Observable<BillingInformation[]> {
    let p = new HttpParams()
      .set('userAccountId', String(params.userAccountId))
      .set('from', params.from)
      .set('to', params.to)
      .set('page', String(params.page ?? 0))
      .set('size', String(params.size ?? 500));
    if (params.groupId !== undefined && params.groupId !== null) {
      p = p.set('groupId', String(params.groupId));
    }
    return this.http
      .get<PageBillingInformation>(`${this.base}/billings/search`, { params: p })
      .pipe(map((page) => page.content ?? []));
  }

  /**
   * Legacy KundenBilling screen is customer-scoped; the real API is UAC-scoped.
   * We fan out over every UAC of the customer and flatten the results.
   */
  searchForUserAccounts(params: {
    userAccountIds: number[];
    from: string;
    to: string;
    groupId?: number;
  }): Observable<BillingInformation[]> {
    if (params.userAccountIds.length === 0) return of([]);
    return forkJoin(
      params.userAccountIds.map((uac) =>
        this.search({
          userAccountId: uac,
          from: params.from,
          to: params.to,
          groupId: params.groupId,
        }),
      ),
    ).pipe(map((lists) => lists.flat()));
  }

  /** Backend accepts a batch; we always send a single-element array. */
  create(message: BillingMessage): Observable<BillingCreated[]> {
    return this.http.post<BillingCreated[]>(`${this.base}/billings`, [message]);
  }

  /** Backend rejects delete if already invoiced (BIL_REID != null). */
  delete(billingId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/billings/${billingId}`);
  }

  /**
   * Delete all callcenter charges of one call document for one user account in a
   * single transaction (EB-4383, replaces KV `deleteCCBillingByDokument`).
   * Uses the legacy feature filter — `call%` minus `call_Forw%`/`callsekret%`,
   * plus `cc_min%` — so it can also remove matching rows outside the callcenter
   * group. `deletedCount: 0` is success. 409 when any affected row is invoiced;
   * the reason is in the string `error` field of the body.
   */
  deleteCallcenterCharges(documentId: number, userAccountId: number): Observable<CallcenterChargesDeleted> {
    const params = new HttpParams()
      .set('documentId', String(documentId))
      .set('userAccountId', String(userAccountId));
    return this.http.delete<CallcenterChargesDeleted>(`${this.base}/billings`, { params });
  }

  /**
   * Resolve the per-feature default unit price for this customer — replicates
   * the legacy `KundeFeature.load(customerId, featureName).getEinheitenPreis()`
   * call that auto-populated the Einheitenpreis field in the Swing
   * "Hinzufügen" form.
   *
   * Uses `GET /contract/v2/rest/features/booked?customerId=&features=...` (op
   * `getExtensionInformation`). The backend runs the same CTE legacy Swing
   * used — joining the customer's live contracts into VERTRAG_FEATURE_OPTIONEN
   * → VERTRAG_TARIF_OPTIONEN → TARIF_TARIFOPTIONEN with priority ordering —
   * so coverage matches legacy 1:1 (unlike the autoplan-check fallback which
   * only saw plan-tier features).
   *
   * Response is a `BookedFeature[]`; we take the lowest `priority` row per
   * feature (mirrors legacy "prio 1 wins" logic in `KundeFeature.load`).
   *
   * Resilient:
   *  - 4xx/5xx → empty map (caller falls back to whatever's in the input)
   *  - Feature not bookable for this customer → omitted from the response →
   *    omitted from the map (caller leaves the Unit Price field alone).
   *
   * @returns Map keyed by feature code (e.g. `callsekret`) → unit price.
   */
  getDefaultFeaturePrices(
    customerId: number,
    featureNames: readonly string[],
  ): Observable<Map<string, number>> {
    if (featureNames.length === 0) return of(new Map<string, number>());
    const cacheKey = `${customerId}|${[...featureNames].sort().join(',')}`;
    const cached = this.defaultPricesCache.get(cacheKey);
    if (cached) return cached;
    let params = new HttpParams().set('customerId', String(customerId));
    for (const name of featureNames) {
      params = params.append('features', name);
    }
    const stream = this.http
      .get<BookedFeature[]>(`${environment.contractUrl}/features/booked`, { params })
      .pipe(
        map((rows) => this.buildFeaturePriceMap(rows ?? [])),
        catchError(() => of(new Map<string, number>())),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    this.defaultPricesCache.set(cacheKey, stream);
    return stream;
  }

  /**
   * Warm the billing-groups cache so the Add Billing dialog opens instantly.
   * Default-feature prices are per-feature-set now (not per-customer), so they
   * can't be usefully pre-warmed without knowing the feature list — the dialog
   * fetches them itself on open. Subscribe-and-forget; errors are swallowed.
   *
   * The unused `customerId` parameter is kept for future use (e.g. if we
   * ever want to pre-warm contract metadata).
   */
  prefetchAddDialogData(_customerId: number): void {
    this.getGroups().subscribe({ error: () => void 0 });
  }

  private buildFeaturePriceMap(rows: BookedFeature[]): Map<string, number> {
    // Keep the row with the lowest `priority` per feature name (mirrors
    // legacy `KundeFeature.load` — VERTRAG_FEATURE_OPTIONEN=1 beats
    // VERTRAG_TARIF_OPTIONEN=2 beats TARIF_TARIFOPTIONEN=3).
    const bestPrio = new Map<string, number>();
    const out = new Map<string, number>();
    for (const row of rows) {
      if (typeof row.name !== 'string' || typeof row.unitPrice !== 'number') continue;
      const prio = typeof row.priority === 'number' ? row.priority : Number.MAX_SAFE_INTEGER;
      const seenPrio = bestPrio.get(row.name);
      if (seenPrio === undefined || prio < seenPrio) {
        bestPrio.set(row.name, prio);
        out.set(row.name, row.unitPrice);
      }
    }
    return out;
  }
}

/**
 * Subset of `BookedFeature` we rely on (see contract-api swagger
 * `feature-controller > getExtensionInformation`). Backend returns one row
 * per (feature, source) combination; `priority` encodes which source wins
 * — lower number = higher priority, matching legacy `KundeFeature.load`.
 */
interface BookedFeature {
  name: string;
  unitPrice: number;
  priority?: number;
  source?: string;
}

/**
 * Convert a `YYYY-MM-DD` date (from a native date input) into an ISO timestamp
 * at day-start or day-end — matches the legacy "Zeitraum" von 00:00:00 / bis 23:59:59 rule.
 */
export function toRangeIso(dateStr: string, endOfDay: boolean): string {
  const suffix = endOfDay ? 'T23:59:59.999Z' : 'T00:00:00.000Z';
  return `${dateStr}${suffix}`;
}
